import { Activity, Battery, Thermometer, Wifi } from "lucide-react";
import { FieldMetric } from "./FieldLayout";
import RadarHistory from "./RadarHistory";
import { fieldTime, fieldValue } from "utils/fieldFormat";
import { formatTilt, tiltFromAxes } from "utils/tilt";

const TILT_HINT =
  "Angle from vertical computed from the X, Y and Z axes. The firmware's own value clamps Z to 1 g and can read 0 for small tilts.";

// `history`: with the radar's distance history (Field cameras); Node Control
// leaves it to Radar analysis.
export default function FieldDiagnostics({ samples = [], history = true }) {
  const latest = samples.reduce(
    (latest, row) =>
      !latest || Date.parse(row.timestamp) > Date.parse(latest.timestamp)
        ? row
        : latest,
    null,
  );
  const raw = latest?.raw || {};
  const flat = {
    ...raw,
    ...Object.fromEntries(
      Object.entries(latest || {}).filter(([, v]) => v !== null),
    ),
  };
  const get = (...keys) => {
    for (const key of keys)
      if (flat[key] !== undefined && flat[key] !== null) return flat[key];
    return undefined;
  };
  const facts = [
    [
      "Power & charging",
      [
        ["Battery", get("battery_voltage", "bat_v"), "V"],
        ["Battery current", get("bi", "battery_current"), "A"],
        ["Battery power", get("bw", "battery_power"), "W"],
        ["Solar voltage", get("sv", "solar_voltage"), "V"],
        ["Solar current", get("si", "solar_current"), "A"],
        ["Solar power", get("sw", "solar_power"), "W"],
        ["Charging state", get("chg", "charging", "charge_state")],
        ["Battery band", get("batt_band", "battery_band", "bat_band")],
      ],
    ],
    [
      "Connection & location",
      [
        ["Transport", get("radio", "transport", "net", "network_type")],
        ["Signal", get("rssi", "signal_dbm"), "dBm"],
        ["Operator", get("op", "operator")],
        ["Network technology", get("tech", "rat", "network_technology")],
        ["Latitude", get("latitude", "lat")],
        ["Longitude", get("longitude", "lon")],
        ["GNSS fix", get("gps_fresh", "gps_valid", "gnss_valid")],
        ["Fix age", get("gps_age", "gps_age_sec", "fix_age_s"), "s"],
        ["Satellites", get("gps_sats", "satellites", "sats")],
        ["HDOP", get("gps_hdop", "hdop")],
      ],
    ],
    [
      "Radar quality",
      [
        ["Radar valid", get("radar_valid")],
        ["Radar status", get("jjy_status", "radar_status", "radar_error")],
        ["Radar polls", get("jjy_polls", "radar_polls", "radar_poll_count")],
        ["Agreeing polls", get("d1_n")],
        ["Minimum distance", get("jjy_min_mm"), "mm"],
        ["Maximum distance", get("jjy_max_mm"), "mm"],
        ["Radar spread", get("jjy_spread_mm"), "mm"],
      ],
    ],
    [
      "Motion & orientation",
      [
        ["Acceleration X", get("ax"), "g"],
        ["Acceleration Y", get("ay"), "g"],
        ["Acceleration Z", get("az"), "g"],
        ["Acceleration magnitude", get("amag"), "g"],
        [
          "Tilt",
          formatTilt(tiltFromAxes(get("ax"), get("ay"), get("az"))),
          "°",
          TILT_HINT,
        ],
        ["Tilt reported by firmware", get("tilt"), "°"],
      ],
    ],
    [
      "Device health",
      [
        ["Firmware", get("firmware", "fw")],
        ["Successful boot confirmed", get("app_confirmed")],
        ["Application state", get("app_ota_state")],
        ["Wake reason", get("wake", "wake_reason")],
        ["Reset reason", get("rst", "reset_reason")],
        ["Free memory", get("heap", "free_heap"), "B"],
        ["RTC temperature", get("rtc_t", "rtc_temp"), "°C"],
        ["Chip temperature", get("chip_t", "chip_temp"), "°C"],
        ["Upload duration", get("t_mqtt", "upload_ms"), "ms"],
        ["Previous awake time", get("pw_ms"), "ms"],
      ],
    ],
    [
      "Camera & upload health",
      [
        ["Buffered readings", get("queue_n", "backlog", "buffer_count")],
        ["Buffer capacity", get("queue_capacity")],
        ["Local storage ready", get("queue_fs_ready")],
        ["Accepted images this boot", get("image_accepted_total")],
        ["Failed images this boot", get("image_failed_total")],
        ["Image retries this boot", get("image_retry_total")],
        ["Cellular use this month", raw.dq?.mc, "KiB"],
        ["Wi-Fi use this month", raw.dq?.mw, "KiB"],
        ["Previous upload sent", raw.dq?.tx, "B"],
        ["Previous upload received", raw.dq?.rx, "B"],
      ],
    ],
  ];
  return (
    <>
      {latest && Date.now() - Date.parse(latest.timestamp) > 3600000 && (
        <div className="field-notice" style={{ marginTop: 20 }}>
          Last telemetry is over an hour old. Values below describe that report,
          not a current measurement.
        </div>
      )}
      <div className="field-metrics">
        <FieldMetric
          icon={Activity}
          label="Radar distance"
          value={
            latest?.radar_valid === false
              ? "Invalid reading"
              : fieldValue(latest?.distance_cm, "cm")
          }
          detail={
            latest
              ? `Reported ${fieldTime(latest.timestamp)} SGT`
              : "Waiting for telemetry"
          }
        />
        <FieldMetric
          icon={Thermometer}
          label="Temperature & humidity"
          value={fieldValue(latest?.temperature, "°C")}
          detail={`Humidity · ${fieldValue(latest?.humidity, "%")}`}
        />
        <FieldMetric
          icon={Battery}
          label="Battery voltage"
          value={fieldValue(latest?.battery_voltage, "V")}
          detail="Voltage is not a state-of-charge estimate"
        />
        <FieldMetric
          icon={Wifi}
          label="Connection"
          value={fieldValue(get("transport", "net", "network_type"))}
          detail={`Signal · ${fieldValue(get("rssi", "signal_dbm"), "dBm")}`}
        />
      </div>
      {history && <RadarHistory samples={samples} />}
      <div className="field-diagnostics">
        {facts.map(([title, rows]) => (
          <section className="field-panel" key={title}>
            <div className="field-panel-head">
              <h2>{title}</h2>
            </div>
            <dl className="field-facts">
              {rows.map(([label, value, unit, hint]) => (
                <div className="field-fact" key={label}>
                  <dt title={hint}>{label}</dt>
                  <dd>{fieldValue(value, unit)}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
      {latest && (
        <details className="field-panel" style={{ marginTop: 20 }}>
          <summary className="field-panel-head" style={{ cursor: "pointer" }}>
            All fields in the latest report
          </summary>
          <div className="field-panel-body">
            <p
              className="field-caption"
              style={{ marginTop: 0, marginBottom: 12 }}
            >
              Optional fields appear only when the device reports them. This
              export preserves their original values.
            </p>
            <pre className="field-raw">{JSON.stringify(raw, null, 2)}</pre>
          </div>
        </details>
      )}
    </>
  );
}
